/**
 * A Location's working week — the pure bits, so they can be checked.
 *
 * ⚠️ WHY THIS FILE EXISTS. These four helpers started life inside `LocationsPage.js`,
 * where the only way to know whether they were right was to click through the app.
 * Rob found two faults in them that way in ten minutes, and reading the code did not
 * show me either one. Anything that decides which days a person works is worth a test
 * seam, and a page component isn't one.
 *
 * Run the checks with:  node src/lib/workingWeek.checks.mjs
 */

export const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
export const MONDAY_TO_FRIDAY = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const DAY_SHORT = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

/**
 * A set of working days -> the AM/PM pattern the rest of the app stores.
 *
 * ⚠️ EVERY DAY IS WRITTEN OUT, enabled or not, so that "no days" survives as no days.
 * The backend's `build_pattern` reads `days = days or [...]` and silently returns
 * Monday-to-Friday for an empty list — the API rejects an all-off week anyway, but
 * this makes sure we never send one by accident.
 */
export const buildWeek = (days) => JSON.stringify(
  Object.fromEntries(DAY_KEYS.map((day) => [day, {
    am: { enabled: days.includes(day), start: '09:00', end: '12:00' },
    pm: { enabled: days.includes(day), start: '13:00', end: '17:00' },
  }]))
);

/**
 * Which days a pattern actually works. A day counts if EITHER session is enabled.
 *
 * ⚠️ TWO SHAPES (found 03/11/2026, the Locations step). A person's or an office's week is
 * `{monday: {am: {enabled}, pm: {enabled}}}`, but the COMPANY's business hours can be
 * `{monday: {enabled, start, end}}` — so "Same as the company" in the Location editor never
 * said which days the company works, because this read nothing from that shape. A day-level
 * `enabled` now counts too.
 */
export const daysWorkedIn = (patternJson) => {
  if (!patternJson) return [];
  let data;
  try { data = typeof patternJson === 'string' ? JSON.parse(patternJson) : patternJson; }
  catch (e) { return []; }
  return DAY_KEYS.filter((d) => data?.[d]?.enabled === true
    || ['am', 'pm'].some((part) => data?.[d]?.[part]?.enabled));
};

/**
 * The same days, starting where the working week starts — the first worked day whose day
 * before is NOT worked — so `describeDays` reads Sunday-to-Thursday as "Sun to Thu" rather
 * than "Mon, Tue, Wed, Thu and Sun". Days in any order in; the same days out.
 */
export const inWeekOrder = (days) => {
  const list = DAY_KEYS.filter((d) => (days || []).includes(d));
  const i = list.findIndex((d) => !list.includes(DAY_KEYS[(DAY_KEYS.indexOf(d) + 6) % 7]));
  return i > 0 ? [...list.slice(i), ...list.slice(0, i)] : list;
};

/**
 * "Sun to Thu" for a contiguous run, otherwise "Mon, Tue, Wed, Thu and Sat".
 *
 * ⚠️ Contiguity is judged in the WEEK'S OWN ORDER, starting from the first day given —
 * not Monday-first. Sunday-to-Thursday is a run; a Monday-first reading would call it
 * "Mon, Tue, Wed, Thu and Sun", which is longer and wrong about the shape.
 */
export const describeDays = (days) => {
  if (!days || !days.length) return 'no days';
  const start = DAY_KEYS.indexOf(days[0]);
  const rotated = [...DAY_KEYS.slice(start), ...DAY_KEYS.slice(0, start)];
  const positions = days.map((d) => rotated.indexOf(d)).sort((a, b) => a - b);
  const contiguous = positions.every((p, i) => i === 0 || p === positions[i - 1] + 1);
  const label = (d) => DAY_SHORT[d] || d;
  if (contiguous && days.length > 2) {
    return `${label(rotated[positions[0]])} to ${label(rotated[positions[positions.length - 1]])}`;
  }
  const names = positions.map((p) => label(rotated[p]));
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names[0];
};

/**
 * Which week to show when the admin turns "Different at this location" back on.
 *
 * ⚠️ THE ORDER IS THE WHOLE POINT, and it is what Rob reported twice:
 *   1. what is already in the form (they never left the override);
 *   2. **their own draft** — the week they had before flicking to "Same as the
 *      company" to compare. Losing this is the bug he found;
 *   3. the company week, as a sensible thing to start editing from;
 *   4. Monday to Friday, only if we have nothing else at all.
 *
 * ⚠️ NEVER RETURNS AN EMPTY WEEK. An all-off pattern would be rejected by the API and,
 * worse, an empty day LIST handed to the backend's `build_pattern` silently becomes
 * Monday to Friday.
 */
export const weekWhenOverridden = (currentWeek, draftWeek, companyWeek) =>
  currentWeek || draftWeek || companyWeek || buildWeek(MONDAY_TO_FRIDAY);

/**
 * What to say after saving a location whose working week moved.
 *
 * ⚠️ IT CLOSES THE LOOP THE PREVIEW OPENS. The preview names who changes; the server
 * re-prices them and reports a count. Saying the count out loud is how an admin sees
 * the promise was kept — and how a mismatch between the two surfaces at test time
 * rather than in a customer's balance months later.
 *
 * ⚠️ "RECHECKED", NOT "CHANGED", and the difference is real. The server recomputes
 * EVERYONE at the location, including people whose own dated pattern means their
 * figure doesn't move — deliberately, so that only one piece of code holds an opinion
 * about who a location applies to. Promising "changed" would overstate it every time
 * somebody at that location has their own week.
 */
export const repricedMessage = (count) => {
  const n = Number(count) || 0;
  if (n <= 0) return 'Location updated successfully';
  return `Location updated — leave rechecked for ${n} ${n === 1 ? 'person' : 'people'}`;
};
